import Transform from '../math/Transform.js';
import DirectionResolver from './DirectionResolver.js';
import AmbientOcclusion from '../ao/AmbientOcclusion.js';
import DOMUtils from '../utils/DOMUtils.js';
import LPCCompositor from './LPCCompositor.js';
import { buildLayerStack } from './LPCLayerDefinitions.js';

/**
 * CharacterSprite - DOM element for rendering a character sprite
 *
 * Creates a single div element that:
 * - Uses CSS transform to position in isometric screen space
 * - Displays the current animation frame using a sprite atlas
 * - Uses background-position to switch frames (no flickering)
 * - Uses 8-directional sprites based on camera angle and character facing
 */
export default class CharacterSprite {
  // Tunable AO parameters
  static AO_LERP_SPEED = 0.08;  // How fast sprite brightness adjusts to AO (0.01-0.2, lower = smoother)
  static AO_MAX_DISTANCE = 0.5; // Max distance from surface to update AO (0-2, higher = updates while jumping)

  // Per-character death drop multiplier (fraction of sprite height to sink)
  // Tunable via debug panel. Key = characterName, value = multiplier (0-1).
  static DEATH_DROP = { hellrat: 0, bear: 0 };
  static DEATH_DROP_DEFAULT = 0.2;

  /**
   * Create a new CharacterSprite
   * @param {Character} character - The character this sprite represents
   * @param {Object} options - Sprite options
   * @param {number} [options.spriteWidth] - Width of sprite in pixels (default 56)
   * @param {number} [options.spriteHeight] - Height of sprite in pixels (default 56)
   * @param {number} [options.scale] - Base scale factor (default 1)
   */
  constructor(character, options = {}) {
    this.character = character;

    // Sprite dimensions (from metadata, default to gandalf's 56x56)
    this.spriteWidth = options.spriteWidth || 56;
    this.spriteHeight = options.spriteHeight || 56;
    this.scale = options.scale || 1;

    // Atlas reference (set via setAtlas)
    this.atlas = null;
    this.onAtlasChange = null; // Callback: (atlas) => void — fired after setAtlas applies

    // Current frame position in atlas (for change detection)
    this.currentBgPosition = null;

    // Current sprite scale (set by update(), used by setFramePosition() to scale atlas coords)
    this._spriteScale = 1;

    // Current direction (cached to avoid unnecessary updates)
    this.currentDirection = null;

    // Create DOM element
    this.element = this.createElement();

    // Reference to world element for appending
    this.worldElement = null;

    // Sprite offset for positioning (set by debug visualizer)
    this.spriteOffset = { x: 0, y: 0, z: 0 };

    // Collision system for AO sampling (set via setCollisionSystem)
    this.collisionSystem = null;

    // Cached AO brightness (0-1) for smooth transitions
    this._aoBrightness = 1;
    this._lastAoUpdate = 0;

    // Water clipping support
    this.submergedElement = null;  // Clone of sprite for submerged portion (backward compat)
    this.submergedFragments = new Map(); // volumeId -> { element, clipInfo } for multi-volume
    this._belowWaterElement = null; // Fragment for below-water portion (behind floating water)
    this._belowWaterClip = null;
    this._currentWaterClip = null; // Current clip path (null = no clipping)
    this._isInWater = false;       // Whether currently clipped by water
    this._originalAboveWaterClip = null; // Un-expanded above-water clip for re-expansion on tile size change

    // LPC compositing support
    this._lpcAppearance = null;    // { bodyType, skinColor, hair, hairColor, eyes, eyeColor }
    this._lpcEquipmentHash = null; // Cache key for current equipment combo
    this._pendingComposite = null; // In-flight composition promise

    // Oversize tile support (weapon attack arcs extend beyond body)
    this._currentTileSize = 0;    // Set by CharacterAnimator per frame
    this._hasOversizeAtlas = false; // Set by setAtlas()
  }

  /**
   * Create the DOM element for the sprite
   * @returns {HTMLElement} The sprite element
   */
  createElement() {
    const el = document.createElement('div');
    el.className = 'character-sprite';
    el.dataset.characterId = this.character.id;

    // Outer element is the positioned container (damage numbers attach here)
    Object.assign(el.style, {
      position: 'absolute',
      width: `${this.spriteWidth}px`,
      height: `${this.spriteHeight}px`,
      pointerEvents: 'none'
    });

    // Inner visual element renders the sprite atlas.
    // Opacity/filter changes target this element so child UI (damage numbers)
    // is not affected by i-frame flicker or corpse fade.
    const visual = document.createElement('div');
    Object.assign(visual.style, {
      position: 'absolute',
      inset: '0',
      imageRendering: 'pixelated',
      backgroundRepeat: 'no-repeat',
      pointerEvents: 'none',
      zIndex: '0'
    });
    el.appendChild(visual);
    this._visual = visual;

    return el;
  }

  /**
   * Enable or disable pointer events on this sprite.
   * NPC sprites need pointer events for click-to-interact.
   * @param {boolean} enabled
   */
  setInteractive(enabled) {
    this.element.style.pointerEvents = enabled ? 'auto' : 'none';
    this.element.style.cursor = enabled ? 'pointer' : '';
  }

  /**
   * Set the collision system for AO-based sprite darkening
   * @param {CharacterCollisionSystem} collisionSystem - The collision system
   */
  setCollisionSystem(collisionSystem) {
    this.collisionSystem = collisionSystem;
  }

  /**
   * Set the sprite atlas
   * @param {SpriteAtlas} atlas - The sprite atlas to use
   */
  setAtlas(atlas) {
    // Release previous LPC atlas reference (refcount-based lifecycle)
    if (this.atlas && this.atlas.release) {
      this.atlas.release();
    }
    this.atlas = atlas;
    // Retain new LPC atlas so its blob URL survives cache eviction
    if (atlas.retain) {
      atlas.retain();
    }

    // Sync sprite dimensions to atlas frame size (handles 96x96 legacy ↔ 64x64 LPC switches).
    // When frame size changes, recalculate _spriteScale and element dimensions immediately
    // so the sprite is display-ready before onAtlasChange reveals it (prevents 1-frame split glitch).
    if (atlas.frameWidth && atlas.frameHeight) {
      const oldHeight = this.spriteHeight;
      this.spriteWidth = atlas.frameWidth;
      this.spriteHeight = atlas.frameHeight;
      if (oldHeight !== this.spriteHeight) {
        if (this._spriteScale !== 1) {
          // Scale was already computed by update() — adjust proportionally
          this._spriteScale *= oldHeight / this.spriteHeight;
        }
        // Always sync element dimensions when frame size changes, even when
        // _spriteScale is 1 (fresh character before first update()). Without
        // this, switching from 96x96 to 64x64 leaves the element oversized
        // and the background-size wrong until the next render pass.
        const sw = this.spriteWidth * this._spriteScale;
        const sh = this.spriteHeight * this._spriteScale;
        this.element.style.width = `${sw}px`;
        this.element.style.height = `${sh}px`;
      }
    }

    // Clear stale background-position from the previous atlas so a forced reflow
    // (e.g., from syncSlidersFromValues in onAtlasChange) can't paint old coordinates
    // on the new atlas, which manifests as a 1-frame "split character" glitch.
    this.currentBgPosition = null;

    // Apply atlas CSS to visual element (use current scale for background-size
    // so it matches the already-scaled element dimensions)
    const s = this._spriteScale;
    Object.assign(this._visual.style, {
      backgroundImage: `url(${atlas.url})`,
      backgroundSize: `${atlas.width * s}px ${atlas.height * s}px`,
      backgroundPosition: '0px 0px'
    });

    // Enable overflow for oversize atlas tiles (weapon attack arcs).
    // Keep inset:'0' on the visual — update() overrides with explicit dims when needed.
    // This prevents a 0×0 visual flash between setAtlas() and the next update() call.
    if (atlas.hasOversize) {
      this._hasOversizeAtlas = true;
      this.element.style.overflow = 'visible';
    } else {
      if (this._hasOversizeAtlas) {
        // Restore inset shorthand — setting explicit left/top in oversize mode
        // breaks the CSSOM shorthand, so clearing longhands alone doesn't work.
        this._visual.style.inset = '0';
        this._visual.style.width = '';
        this._visual.style.height = '';
      }
      this._hasOversizeAtlas = false;
      this.element.style.overflow = '';
    }

    this.onAtlasChange?.(atlas);
  }

  /**
   * Attach the sprite to a parent element
   * @param {HTMLElement} parent - Parent element (usually #world)
   */
  attachTo(parent) {
    this.worldElement = parent;
    // Attach to #world for proper depth sorting with cuboids
    parent.appendChild(this.element);
  }

  /**
   * Remove the sprite from the DOM
   */
  detach() {
    if (this.element.parentElement) {
      this.element.parentElement.removeChild(this.element);
    }
    this.worldElement = null;
  }

  /**
   * Set the current frame using atlas position
   * @param {string} bgPosition - CSS background-position value (e.g., "-56px -112px")
   */
  /**
   * Update the current tile size and immediately sync the visual dimensions.
   * Called by CharacterAnimator when the frame's tileSize changes (e.g.,
   * switching from a 192px oversize attack frame to a 64px idle frame).
   * Without this, the visual dims lag one frame behind — showing atlas
   * neighbours for a flash when animation state changes mid-frame.
   */
  setTileSize(tileSize) {
    if (tileSize === this._currentTileSize) return;
    this._currentTileSize = tileSize;
    if (this._hasOversizeAtlas && this._spriteScale) {
      const bodySize = this.atlas.bodyFrameSize || this.spriteWidth;
      const ratio = tileSize / bodySize;
      const scaledW = this.spriteWidth * this._spriteScale;
      const scaledH = this.spriteHeight * this._spriteScale;
      const visualW = scaledW * ratio;
      const visualH = scaledH * ratio;
      const offsetX = (visualW - scaledW) / 2;
      const offsetY = (visualH - scaledH) / 2;
      this._visual.style.left = `${-offsetX}px`;
      this._visual.style.top = `${-offsetY}px`;
      this._visual.style.width = `${visualW}px`;
      this._visual.style.height = `${visualH}px`;
    }
    // Re-expand the water clip-path when tile size changes while in water.
    // The sort may not re-run (character standing still), so the clip-path
    // from the previous tile size would clip the new larger/smaller visual.
    if (this._isInWater && this._originalAboveWaterClip) {
      this.element.style.clipPath = this._expandClipForOversize(this._originalAboveWaterClip);
      // Also re-sync fragments (their size depends on current tile size)
      for (const frag of this.submergedFragments.values()) {
        this.syncFragmentElement(frag);
      }
      if (this._belowWaterElement && this._belowWaterClip) {
        this.syncFragmentElement({ element: this._belowWaterElement, clipInfo: { submergedClipPath: this._belowWaterClip } });
      }
    }
  }

  setFramePosition(bgPosition) {
    if (bgPosition !== this.currentBgPosition) {
      this.currentBgPosition = bgPosition;
      // Apply current sprite scale so atlas coords match the scaled background-size.
      // sprite.update() sets _spriteScale; between render passes the animator may
      // advance frames without sprite.update() running (idle + static camera).
      if (this._spriteScale !== 1 && bgPosition) {
        const match = bgPosition.match(/-?(\d+)px\s+-?(\d+)px/);
        if (match) {
          const scaledX = parseInt(match[1]) * this._spriteScale;
          const scaledY = parseInt(match[2]) * this._spriteScale;
          this._visual.style.backgroundPosition = `-${scaledX}px -${scaledY}px`;
          return;
        }
      }
      this._visual.style.backgroundPosition = bgPosition;
    }
  }

  /**
   * Update the sprite direction based on character facing and camera angle
   * @param {number} cameraAngle - Current camera angle
   * @returns {string} The new direction, or null if unchanged
   */
  updateDirection(cameraAngle) {
    const relativeAngle = ((this.character.facingAngle - cameraAngle) % 360 + 360) % 360;

    if (this.currentDirection !== null) {
      // Apply hysteresis: only switch direction if we've moved far enough
      // past the boundary from our current direction's center angle
      const currentIndex = DirectionResolver.DIRECTIONS.indexOf(this.currentDirection);
      const currentCenter = currentIndex * DirectionResolver.ANGLE_STEP;
      let diff = relativeAngle - currentCenter;
      // Normalize to -180..180
      if (diff > 180) diff -= 360;
      if (diff < -180) diff += 360;

      // Stay in current direction if within half-step + hysteresis
      if (Math.abs(diff) <= DirectionResolver.HALF_STEP + DirectionResolver.HYSTERESIS) {
        return null;
      }
    }

    // Resolve new direction (snap to nearest 45-degree direction)
    const newDirection = DirectionResolver.resolve(
      this.character.facingAngle,
      cameraAngle
    );

    if (newDirection !== this.currentDirection) {
      this.currentDirection = newDirection;
      return newDirection;
    }

    return null;
  }

  /**
   * Update the sprite's screen position and scale
   * @param {Object} camera - Camera object
   */
  update(camera) {
    const char = this.character;
    const offset = this.spriteOffset || { x: 0, y: 0, z: 0, scale: 1 };
    const scaleMultiplier = offset.scale || 1;

    // Tick hit flash timer (set by combat system)
    if (this._hitFlashTimer > 0) {
      this._hitFlashTimer -= 0.016; // ~1 frame at 60fps
    }

    // Update direction based on character facing and camera angle
    const cameraAngle = camera.angle || 0;
    const newDirection = this.updateDirection(cameraAngle);

    // Notify animator if direction changed
    if (newDirection && char.animator) {
      char.animator.onDirectionChange(newDirection);
    }

    // On Android, zoom is baked into positions/sizes (fixes clipping issues)
    // On other platforms, CSS scale on #world handles zoom
    const isAndroid = DOMUtils.isAndroid();
    const zoom = isAndroid ? camera.zoomLevel : 1;
    const effectiveCubeSize = camera.CUBE_SIZE * zoom;

    // Apply sprite offset to world position (offset is in world units)
    const worldX = (char.x + offset.x) * effectiveCubeSize;
    const worldY = (char.y + offset.y) * effectiveCubeSize;
    const worldZ = -(char.z + offset.z) * effectiveCubeSize;

    // Calculate screen position
    // On Android: use zoomed coordinates and scale camera position
    const screenPos = Transform.worldToScreen(
      worldX,
      worldY,
      worldZ,
      camera,
      isAndroid ? zoom : 1  // Pass zoom for camera position scaling on Android
    );

    // Calculate sprite display size with scale multiplier
    const targetHeight = char.height * effectiveCubeSize * scaleMultiplier;
    const spriteScale = targetHeight / this.spriteHeight;
    this._spriteScale = spriteScale; // Cache for setFramePosition() between render passes
    const scaledWidth = this.spriteWidth * spriteScale;
    const scaledHeight = this.spriteHeight * spriteScale;

    // Scale the background-size proportionally with the element
    if (this.atlas) {
      const bgWidth = this.atlas.width * spriteScale;
      const bgHeight = this.atlas.height * spriteScale;
      this._visual.style.backgroundSize = `${bgWidth}px ${bgHeight}px`;

      // Oversize tile support: expand visual beyond element for weapon arcs.
      // The element stays body-sized (64*scale) for correct positioning;
      // the visual extends to tile-sized (e.g., 192*scale) centered on the body.
      if (this._hasOversizeAtlas) {
        const bodySize = this.atlas.bodyFrameSize || this.spriteWidth;
        const ts = this._currentTileSize || bodySize;
        const ratio = ts / bodySize;
        const visualW = scaledWidth * ratio;
        const visualH = scaledHeight * ratio;
        const offsetX = (visualW - scaledWidth) / 2;
        const offsetY = (visualH - scaledHeight) / 2;
        this._visual.style.left = `${-offsetX}px`;
        this._visual.style.top = `${-offsetY}px`;
        this._visual.style.width = `${visualW}px`;
        this._visual.style.height = `${visualH}px`;
      }

      // Scale the background position as well
      if (this.currentBgPosition) {
        const match = this.currentBgPosition.match(/-?(\d+)px -?(\d+)px/);
        if (match) {
          const origX = parseInt(match[1]);
          const origY = parseInt(match[2]);
          const scaledBgX = origX * spriteScale;
          const scaledBgY = origY * spriteScale;
          this._visual.style.backgroundPosition = `-${scaledBgX}px -${scaledBgY}px`;
        }
      }
    }

    // Sprites are now in #world (centered container) - position relative to world origin
    // Round to whole pixels to prevent subpixel rendering artifacts (sprite flickering)
    const finalLeft = Math.round(screenPos.x - scaledWidth / 2);
    const finalTop = Math.round(screenPos.y - scaledHeight);

    // Sample AO at character position for darkening
    const aoBrightness = this._sampleAOBrightness(char);

    // Hit flash timer (set by CombatManager/ProjectileManager on damage)
    // During i-frames, soften AO so characters stay shadowed but don't vanish
    // when combined with the opacity pulse trough.
    let filterValue = '';
    const isInIFrames = char.combatStats?.isInvulnerable;
    const effectiveAO = isInIFrames ? aoBrightness + (1 - aoBrightness) * 0.5 : aoBrightness;
    const aoFilter = effectiveAO < 1 ? `brightness(${effectiveAO})` : '';
    if (this._hitFlashTimer > 0) {
      filterValue = aoFilter ? `brightness(3) ${aoFilter}` : 'brightness(3)';
    } else {
      filterValue = aoFilter;
    }

    // Death drop: smoothly slide sprite down so the corpse frame sits near ground level
    let transformValue = '';
    if (char.combatStats && char.combatStats.isDead) {
      const now = performance.now();
      if (this._deathOffsetY === undefined) {
        this._deathOffsetY = 0;
        this._deathLastTime = now;
      }
      const dt = Math.min((now - this._deathLastTime) / 1000, 0.1); // seconds, capped
      this._deathLastTime = now;
      const dropMul = CharacterSprite.DEATH_DROP[char.characterName] ?? CharacterSprite.DEATH_DROP_DEFAULT;
      const targetPx = scaledHeight * dropMul;
      const speed = scaledHeight * 0.4; // pixels per second — slow sink over ~0.5s
      this._deathOffsetY = Math.min(targetPx, this._deathOffsetY + speed * dt);
      transformValue = `translateY(${this._deathOffsetY}px)`;
    } else if (this._deathOffsetY !== undefined) {
      delete this._deathOffsetY;
      delete this._deathLastTime;
    }

    Object.assign(this.element.style, {
      width: `${scaledWidth}px`,
      height: `${scaledHeight}px`,
      left: `${finalLeft}px`,
      top: `${finalTop}px`,
      transform: transformValue
    });
    this._visual.style.filter = filterValue;

    // Sync submerged and below-water fragments with main sprite when in water
    // This ensures fragments stay aligned even during transform-only updates
    if (this._isInWater && this.submergedFragments.size > 0) {
      for (const frag of this.submergedFragments.values()) {
        this.syncFragmentElement(frag);
      }
      if (this._belowWaterElement && this._belowWaterClip) {
        this.syncFragmentElement({ element: this._belowWaterElement, clipInfo: { submergedClipPath: this._belowWaterClip } });
      }
    }
  }

  /**
   * Sample AO brightness at character position (efficiently)
   * @param {Character} char - The character
   * @returns {number} Brightness value (0-1, where 1 = fully lit)
   */
  _sampleAOBrightness(char) {
    // Skip if AO disabled or no collision system
    if (!AmbientOcclusion.enabled || !this.collisionSystem) {
      return 1;
    }

    // Find the closest surface cuboid
    const result = this._findFloorCuboid(char);
    if (!result) {
      // No surface found - keep current brightness (don't change)
      return this._aoBrightness;
    }

    const { cuboid: floorCuboid, distance } = result;

    // Only update AO target when close to the surface (standing/near ground)
    // When jumping/falling, keep current brightness until we land
    if (distance > CharacterSprite.AO_MAX_DISTANCE) {
      return this._aoBrightness;
    }

    // Sample AO at character's XZ position on the floor's top face
    const targetBrightness = AmbientOcclusion.sampleAOAtWorldPoint(
      floorCuboid,
      'top',
      char.x,
      char.z
    );

    // Smooth transition - gradually tend toward target
    this._aoBrightness += (targetBrightness - this._aoBrightness) * CharacterSprite.AO_LERP_SPEED;

    return this._aoBrightness;
  }

  /**
   * Find the closest surface cuboid to the character's feet
   * Uses character footprint overlap, not just center point
   * @param {Character} char - The character
   * @returns {{cuboid: Cuboid, distance: number}|null} The floor cuboid and distance, or null
   */
  _findFloorCuboid(char) {
    // Character footprint (use character dimensions or defaults)
    const halfWidth = (char.width || 0.5) * 0.5;
    const halfDepth = (char.depth || 0.5) * 0.5;

    // Query box covering character footprint and a range above/below
    const queryBox = {
      minX: char.x - halfWidth,
      maxX: char.x + halfWidth,
      minY: char.y - 3.0,  // Check below (enough for jumps/falls)
      maxY: char.y + 0.5,  // Check slightly above (for surfaces we're standing on)
      minZ: char.z - halfDepth,
      maxZ: char.z + halfDepth
    };

    const candidates = this.collisionSystem.queryCuboids(queryBox);

    // Find the surface closest to character's feet (by Y distance)
    let bestCuboid = null;
    let bestDistance = Infinity;

    for (const cuboid of candidates) {
      const topY = cuboid.y + cuboid.height;

      // Check if character footprint overlaps with cuboid's top face
      const overlapX = char.x + halfWidth > cuboid.x && char.x - halfWidth < cuboid.x + cuboid.width;
      const overlapZ = char.z + halfDepth > cuboid.z && char.z - halfDepth < cuboid.z + cuboid.depth;

      if (!overlapX || !overlapZ) continue;

      // Distance from character feet to this surface
      const distance = char.y - topY;

      // Only consider surfaces at or below character (with small tolerance)
      if (distance >= -0.1 && distance < bestDistance) {
        bestCuboid = cuboid;
        bestDistance = distance;
      }
    }

    if (!bestCuboid) return null;

    return { cuboid: bestCuboid, distance: Math.max(0, bestDistance) };
  }

  /**
   * Get the current sprite direction
   * @returns {string} Current direction string
   */
  getDirection() {
    return this.currentDirection;
  }

  /**
   * Set sprite visibility
   * @param {boolean} visible - Whether sprite should be visible
   */
  setVisible(visible) {
    this.element.style.display = visible ? 'block' : 'none';
  }

  /**
   * Set sprite opacity
   * @param {number} opacity - Opacity value (0-1)
   */
  setOpacity(opacity) {
    this._visual.style.opacity = opacity;
  }

  /**
   * Get the depth value for sorting (based on character position)
   * @param {Object} camera - Camera object
   * @returns {number} Depth value
   */
  getDepth(camera) {
    // Calculate depth based on screen Y position (lower = closer to camera)
    const rotation = Transform.getCameraRotation(camera);
    const yScale = Transform.calculateYScale(camera.skewAngle);

    // Project character position to get depth
    const worldX = this.character.x * camera.CUBE_SIZE;
    const worldZ = -this.character.z * camera.CUBE_SIZE;

    const relX = worldX - camera.position.x;
    const relZ = worldZ - camera.position.z;

    // Depth is the Z component after rotation
    return (relX * rotation.sin + relZ * rotation.cos) * yScale;
  }

  // ==================== WATER CLIPPING ====================

  /**
   * Create the submerged element (clone of main sprite for underwater portion)
   * @returns {HTMLElement} The submerged element
   */
  createSubmergedElement() {
    const el = document.createElement('div');
    el.className = 'character-sprite submerged';
    el.dataset.characterId = this.character.id;
    el.dataset.submerged = 'true';

    // Copy styling from main element
    Object.assign(el.style, {
      position: 'absolute',
      imageRendering: 'pixelated',
      backgroundRepeat: 'no-repeat',
      pointerEvents: 'none'
    });

    return el;
  }

  /**
   * Ensure the submerged element exists
   */
  ensureSubmergedElement() {
    if (!this.submergedElement) {
      this.submergedElement = this.createSubmergedElement();
    }
  }

  /**
   * Sync submerged element with main sprite appearance
   * (position, size, background, etc.)
   */
  syncSubmergedElement() {
    if (!this.submergedElement) return;

    const main = this.element;
    const vis = this._visual;
    const sub = this.submergedElement;

    // Copy visual properties (background lives on _visual, position on element)
    sub.style.width = main.style.width;
    sub.style.height = main.style.height;
    sub.style.left = main.style.left;
    sub.style.top = main.style.top;
    sub.style.backgroundImage = vis.style.backgroundImage;
    sub.style.backgroundSize = vis.style.backgroundSize;
    sub.style.backgroundPosition = vis.style.backgroundPosition;
    sub.style.filter = vis.style.filter;
  }

  /**
   * Expand a CSS clip-path polygon so it covers the oversized visual area.
   * When an oversize atlas is active the visual extends beyond the element via
   * overflow:visible; clip-path percentages are relative to the element, so
   * boundary values (0% / 100%) must be pushed outward to avoid clipping the
   * weapon-arc overflow.  Interior values (surface line, wall positions) are
   * between 0 and 100 exclusive and are left untouched.
   */
  _expandClipForOversize(clipPath) {
    if (!this._hasOversizeAtlas || !clipPath) return clipPath;
    const bodySize = this.atlas?.bodyFrameSize || this.spriteWidth;
    const ts = this._currentTileSize || bodySize;
    if (ts <= bodySize) return clipPath;

    const overflow = ((ts / bodySize) - 1) / 2 * 100; // e.g. 100 for 3× ratio

    return clipPath.replace(/polygon\(([^)]+)\)/g, (_match, pointsStr) => {
      const expanded = pointsStr.replace(/([\d.]+)%/g, (m, val) => {
        const n = parseFloat(val);
        if (n < 0.01) return `${(-overflow).toFixed(2)}%`;
        if (n > 99.99) return `${(100 + overflow).toFixed(2)}%`;
        return m;
      });
      return `polygon(${expanded})`;
    });
  }

  /**
   * Apply water clipping to sprite
   * @param {string} aboveWaterClip - CSS clip-path for above-water portion (main sprite)
   * @param {string} submergedClip - CSS clip-path for submerged portion
   */
  applyWaterClip(aboveWaterClip, submergedClip) {
    this._isInWater = true;
    this._currentWaterClip = { aboveWaterClip, submergedClip };
    this._originalAboveWaterClip = aboveWaterClip;

    // Ensure submerged element exists
    this.ensureSubmergedElement();

    // Apply clip to main sprite (above water) — expand for oversize weapon arcs
    this.element.style.clipPath = this._expandClipForOversize(aboveWaterClip);

    // Apply clip to submerged element
    this.submergedElement.style.clipPath = submergedClip;

    // Sync appearance
    this.syncSubmergedElement();
  }

  /**
   * Apply water clipping for multiple water volumes.
   * Creates/reuses fragments for each volume and applies appropriate clips.
   * @param {string} aboveWaterClip - CSS clip-path for above-water portion (main sprite)
   * @param {Object[]} volumeClipInfos - Array of clip info objects (one per volume)
   */
  applyMultiVolumeWaterClip(aboveWaterClip, volumeClipInfos) {
    this._isInWater = true;
    this._originalAboveWaterClip = aboveWaterClip;
    const activeIds = new Set();

    for (const clipInfo of volumeClipInfos) {
      const volumeId = clipInfo.volumeId;
      activeIds.add(volumeId);

      // Get or create fragment for this volume
      let frag = this.submergedFragments.get(volumeId);
      if (!frag) {
        frag = { element: this.createSubmergedElement(), clipInfo: null };
        this.submergedFragments.set(volumeId, frag);
      }

      // Update fragment
      frag.clipInfo = clipInfo;
      this.syncFragmentElement(frag);
    }

    // Remove stale fragments (volumes character is no longer in)
    for (const [id, frag] of this.submergedFragments) {
      if (!activeIds.has(id)) {
        if (frag.element.parentNode) {
          frag.element.parentNode.removeChild(frag.element);
        }
        this.submergedFragments.delete(id);
      }
    }

    // Apply combined above-water clip to main sprite — expand for oversize weapon arcs
    this.element.style.clipPath = this._expandClipForOversize(aboveWaterClip);

    // Update backward-compat submergedElement reference
    if (this.submergedFragments.size > 0) {
      this.submergedElement = this.submergedFragments.values().next().value.element;
    }
  }

  /**
   * Transform a clip-path from body-space percentages to visual-space
   * percentages for an oversized fragment.  Boundary values (0%/100%) stay
   * at the visual edges; interior values (surface, walls) are scaled into
   * the body region within the larger visual.
   */
  _transformClipToVisualSpace(clipPath, ratio) {
    if (ratio <= 1 || !clipPath) return clipPath;
    const scale = 1 / ratio;
    const offset = (1 - scale) / 2 * 100;
    return clipPath.replace(/polygon\(([^)]+)\)/g, (_match, pointsStr) => {
      const transformed = pointsStr.replace(/([\d.]+)%/g, (m, val) => {
        const n = parseFloat(val);
        if (n < 0.01) return '0%';
        if (n > 99.99) return '100%';
        return `${(n * scale + offset).toFixed(2)}%`;
      });
      return `polygon(${transformed})`;
    });
  }

  /**
   * Sync a fragment element with main sprite appearance
   * @param {Object} frag - Fragment object { element, clipInfo }
   */
  syncFragmentElement(frag) {
    const fragElement = frag.element;
    const clipInfo = frag.clipInfo;
    const main = this.element;
    const vis = this._visual;
    fragElement.style.transform = main.style.transform;
    fragElement.style.backgroundImage = vis.style.backgroundImage;
    fragElement.style.backgroundSize = vis.style.backgroundSize;
    fragElement.style.filter = vis.style.filter;
    fragElement.style.opacity = vis.style.opacity;

    // For oversize frames, make the fragment visual-sized so the full weapon
    // arc renders underwater (not just the body portion).
    const bodySize = this.atlas?.bodyFrameSize || this.spriteWidth;
    const ts = this._currentTileSize || bodySize;
    if (this._hasOversizeAtlas && ts > bodySize) {
      const s = this._spriteScale || 1;
      const ratio = ts / bodySize;
      const bodyW = this.spriteWidth * s;
      const bodyH = this.spriteHeight * s;
      const visualW = bodyW * ratio;
      const visualH = bodyH * ratio;
      const offsetX = (visualW - bodyW) / 2;
      const offsetY = (visualH - bodyH) / 2;

      fragElement.style.width = `${visualW}px`;
      fragElement.style.height = `${visualH}px`;
      fragElement.style.left = `${parseFloat(main.style.left) - offsetX}px`;
      fragElement.style.top = `${parseFloat(main.style.top) - offsetY}px`;
      // Use the visual's background-position directly (already accounts for tile)
      fragElement.style.backgroundPosition = vis.style.backgroundPosition;
      // Transform clip-path from body-space to visual-space
      if (clipInfo?.submergedClipPath) {
        fragElement.style.clipPath = this._transformClipToVisualSpace(
          clipInfo.submergedClipPath, ratio
        );
      }
    } else {
      // Body-sized fragment (standard or non-oversize frame)
      fragElement.style.left = main.style.left;
      fragElement.style.top = main.style.top;
      fragElement.style.width = main.style.width;
      fragElement.style.height = main.style.height;
      fragElement.style.backgroundPosition = vis.style.backgroundPosition;
      if (clipInfo?.submergedClipPath) {
        fragElement.style.clipPath = clipInfo.submergedClipPath;
      }
    }
  }

  /**
   * Get all submerged fragments (for depth sorting in World.js)
   * @returns {Object[]} Array of { element, clipInfo } objects
   */
  getSubmergedFragments() {
    return Array.from(this.submergedFragments.values());
  }

  /**
   * Get or create the below-water fragment element.
   * Used when floating water clips the sprite — the below-water portion
   * sorts behind water while the main sprite (above-water) sorts in front.
   */
  getBelowWaterElement() {
    if (!this._belowWaterElement) {
      this._belowWaterElement = this.createSubmergedElement();
      this._belowWaterElement.dataset.submerged = 'below-water';
    }
    return this._belowWaterElement;
  }

  /**
   * Sync below-water fragment with main sprite appearance and apply clip.
   */
  syncBelowWaterFragment(clipPath) {
    const el = this.getBelowWaterElement();
    this._belowWaterClip = clipPath;
    this.syncFragmentElement({ element: el, clipInfo: { submergedClipPath: clipPath } });
  }

  /**
   * Hide and detach the below-water fragment.
   */
  clearBelowWaterFragment() {
    this._belowWaterClip = null;
    if (this._belowWaterElement) {
      this._belowWaterElement.style.display = 'none';
      if (this._belowWaterElement.parentNode) {
        this._belowWaterElement.parentNode.removeChild(this._belowWaterElement);
      }
    }
  }

  /**
   * Clear water clipping from sprite
   */
  clearWaterClip() {
    if (!this._isInWater) return;

    this._isInWater = false;
    this._currentWaterClip = null;
    this._originalAboveWaterClip = null;

    // Remove clip from main sprite
    this.element.style.clipPath = '';

    // Clean up all submerged fragments
    for (const [id, frag] of this.submergedFragments) {
      if (frag.element.parentNode) {
        frag.element.parentNode.removeChild(frag.element);
      }
    }
    this.submergedFragments.clear();

    // Clean up below-water fragment
    this.clearBelowWaterFragment();

    // Also hide legacy submergedElement if it exists separately
    if (this.submergedElement) {
      this.submergedElement.style.display = 'none';
      this.submergedElement.style.clipPath = '';
    }
  }

  /**
   * Check if sprite is currently in water (clipped)
   * @returns {boolean} True if in water
   */
  isInWater() {
    return this._isInWater;
  }

  /**
   * Get the submerged element (for depth sorting)
   * @returns {HTMLElement|null} The submerged element or null
   */
  getSubmergedElement() {
    return this.submergedElement;
  }

  /**
   * Attach the submerged element to the world
   * Should be called when adding to depth sorting
   * @param {HTMLElement} parent - Parent element (usually #world)
   */
  attachSubmergedTo(parent) {
    if (this.submergedElement && !this.submergedElement.parentElement) {
      parent.appendChild(this.submergedElement);
    }
  }

  /**
   * Detach the submerged element from DOM
   */
  detachSubmerged() {
    if (this.submergedElement && this.submergedElement.parentElement) {
      this.submergedElement.parentElement.removeChild(this.submergedElement);
    }
  }

  // ==================== LPC EQUIPMENT COMPOSITING ====================

  /**
   * Set the LPC appearance config for this character.
   * Call this once at character creation or when appearance changes.
   * @param {Object} appearance - { bodyType, skinColor, hair, hairColor, eyes, eyeColor }
   */
  setLPCAppearance(appearance) {
    this._lpcAppearance = appearance;
  }

  /**
   * Get the current LPC appearance config
   * @returns {Object|null}
   */
  getLPCAppearance() {
    return this._lpcAppearance;
  }

  /**
   * Update the character's visual equipment layers.
   * Called when inventory equipment changes. Recomposites the sprite atlas
   * with the new equipment layers on top of the base body.
   *
   * @param {Object} equippedItems - Map of slot → item definition (must have lpcLayer field)
   */
  async updateEquipment(equippedItems) {
    if (!this._lpcAppearance) return; // Not an LPC character

    // Build layer stack from appearance + equipment
    const layers = buildLayerStack(this._lpcAppearance, equippedItems);
    if (layers.length === 0) return;

    // Generate hash to check if we actually need to recomposite
    const hash = layers.map(l => `${l.basePath}:${l.variant}:${l.zPos}`).join('|');
    if (hash === this._lpcEquipmentHash) return; // No change
    this._lpcEquipmentHash = hash;

    // Get the trixel animation names this character uses
    const baseTrixelAnims = this.character.metadata
      ? this.character.metadata.animationNames
      : ['walking-8-frames', 'breathing-idle', 'running-8-frames'];

    // Always include weapon attack and combat reaction animations so all
    // variants are composited into the atlas for immediate use
    const EXTRA_ANIMS = [
      'weapon-slash', 'weapon-slash-reverse', 'weapon-thrust',
      'weapon-backslash', 'weapon-halfslash', 'weapon-shoot',
      'taking-punch', 'falling-back-death'
    ];
    const trixelAnims = [...new Set([...baseTrixelAnims, ...EXTRA_ANIMS])];

    // Compose new atlas (async — may take 50-100ms)
    try {
      const compositePromise = LPCCompositor.compose(layers, trixelAnims);
      this._pendingComposite = compositePromise;

      const newAtlas = await compositePromise;

      // Check if a newer composition started while we were waiting
      if (this._pendingComposite !== compositePromise) return;
      this._pendingComposite = null;

      // Apply the new atlas. Don't destroy the old one — it lives in
      // LPCCompositor's atlas cache and may be returned again if the
      // player re-equips the same combo. The cache handles eviction/destroy.
      newAtlas._isLPC = true;
      this.setAtlas(newAtlas);

      // Also update the animator's atlas reference and force a frame redraw
      // so the display immediately reflects the new atlas layout
      if (this.character.animator) {
        this.character.animator.setAtlas(newAtlas);
        this.character.animator.updateSpriteFrame();
      }
    } catch (err) {
      // Composition failed — keep current atlas
      this._pendingComposite = null;
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.detach();
    this.detachSubmerged();
    // Release LPC atlas reference so evicted atlases can clean up their blob URLs
    if (this.atlas && this.atlas.release) {
      this.atlas.release();
    }
    this.atlas = null;
    this.character = null;
    this.element = null;
    this.submergedElement = null;
  }
}
